import { Monitor, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme, type Theme } from '@/contexts/ThemeContext';

const themeOptions: { value: Theme; icon: typeof Sun }[] = [
  { value: 'light', icon: Sun },
  { value: 'dark', icon: Moon },
  { value: 'system', icon: Monitor },
];

export function ThemeSwitcher(): JSX.Element {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();

  const currentLabel =
    theme === 'light'
      ? t('header.themeLight')
      : theme === 'dark'
        ? t('header.themeDark')
        : t('header.themeSystem');

  const CurrentIcon = themeOptions.find((o) => o.value === theme)?.icon ?? Monitor;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          aria-label={t('header.theme')}
        >
          <CurrentIcon className="h-4 w-4" />
          <span className="text-xs font-medium">{currentLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {themeOptions.map((option) => {
          const Icon = option.icon;
          return (
            <DropdownMenuItem
              key={option.value}
              onClick={() => setTheme(option.value)}
            >
              <Icon className="mr-2 h-4 w-4" />
              {option.value === 'light'
                ? t('header.themeLight')
                : option.value === 'dark'
                  ? t('header.themeDark')
                  : t('header.themeSystem')}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
